import React, { Component, createRef } from 'react';
import { connect } from 'react-redux';
import posed from 'react-pose';
import styled from '@emotion/styled';
import { compose, multiplyBy } from 'helpers/utils';

import { CoordinatePoint } from 'types/point';
import { RootState } from 'reducers/rootReducer';
import { messages } from 'config/messages';
import { distance } from 'helpers/geometry';
import { formatLinearLabel } from 'helpers/pixelsToDistance';
import { actions as resizeSketchModalActions } from 'ducks/modal/resizeSketchModal';
import { actions as resizeSketchActions } from 'ducks/bluePrintImage/resizeSketch';
import { SelectedLine, selectors as selectionSelectors } from 'ducks/selection/selection';
import { selectors as settingsSelectors } from 'ducks/settings';
import { ReactComponent as IconClose } from 'assets/icons/x.svg';
import { positionedModal, fade } from 'components/animations';
import { ModalWallResize, ButtonBig, ModalBackdrop } from 'components/modal/Modal';
import { sidebarItemButtonStyles } from 'components/sidebar/styles';

const Backdrop = styled(ModalBackdrop)({
  display: 'flex',
  flexFlow: 'column nowrap',
  alignItems: 'center',
  justifyContent: 'center',
});

const BackdropAnimated = posed(Backdrop)(fade);

const ModalAnimated = posed(ModalWallResize)(positionedModal);
const CloseButton = styled.button(sidebarItemButtonStyles);

interface StateProps {
  readonly canResizeSketch: boolean;
  readonly selectedWall?: SelectedLine;
  readonly unitOfMeasure: string;
  readonly precision: number;
  readonly gridSizeInFeet: number;
}
interface ActionProps {
  readonly close: () => void;
  readonly resizeSketch: typeof resizeSketchActions.resizeSketch;
}

type Props = StateProps & ActionProps;

interface State {
  readonly size: string;
  readonly position: ResizePosition;
}

interface ResizePosition {
  readonly top?: number;
  readonly left?: number;
}

const computeModalPosition = (
  {
    top, left, width, height,
  }: ClientRect,
  modalHeight: number,
): ResizePosition => ({
  top: top + height / 2 - modalHeight / 2,
  left: left + width / 2 + 20,
});

class ResizeSketchModal extends Component<Props, State> {
  // eslint-disable-next-line react/sort-comp
  private readonly focusRef: React.RefObject<HTMLInputElement>;

  private readonly modalRef: React.RefObject<HTMLInputElement>;

  public constructor(props: Props) {
    super(props);
    this.focusRef = createRef();
    this.modalRef = createRef();

    this.state = {
      size: '',
      position: this.getModalPosition(),
    };
  }

  public componentDidMount(): void {
    const position = this.getModalPosition();
    this.setState({ position });
    this.focusRef.current!.focus();
  }

  public componentDidUpdate(prevProps: Props): void {
    const { selectedWall, canResizeSketch } = this.props;
    if (prevProps.selectedWall !== selectedWall && selectedWall) {
      const position = this.getModalPosition();
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ position });
    }
    if (prevProps.canResizeSketch && !canResizeSketch) {
      this.close();
    }
  }

  private getModalPosition(): ResizePosition {
    const { selectedWall } = this.props;
    const wallNode = document.getElementById(selectedWall!.wall.wallId);

    if (wallNode) {
      const modalNode = this.modalRef.current;
      const position = computeModalPosition(
        wallNode.getBoundingClientRect(),
        modalNode ? modalNode.getBoundingClientRect().height : 0,
      );
      return position;
    }
    return {};
  }

  private save = (event?: React.KeyboardEvent<HTMLInputElement> | React.FormEvent<HTMLFormElement>): void => {
    if (event) {
      event.preventDefault();
    }

    const { resizeSketch, selectedWall, close } = this.props;
    if (!selectedWall) {
      return;
    }

    const { size } = this.state;
    resizeSketch(selectedWall.wall.wallId, size);
    close();
  };

  private close = (): void => {
    const { close } = this.props;
    close();
  };

  private handleSizeChanged = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const size = event.target.value;
    this.setState(() => ({ size }));
  };

  private handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    event.stopPropagation();
    if (event.key === 'Enter') {
      this.save();
    }
    if (event.key === 'Escape') {
      this.close();
    }
  };

  public render = (): JSX.Element | null => {
    const {
      canResizeSketch, selectedWall, unitOfMeasure, precision, gridSizeInFeet,
    } = this.props;
    const { position, size } = this.state;

    if (!selectedWall || !canResizeSketch) {
      return null;
    }

    const cancelEvent = (event: React.PointerEvent | React.WheelEvent | React.DragEvent): void => {
      event.preventDefault();
      event.stopPropagation();
    };

    const wallStart: CoordinatePoint = selectedWall.points.get(0)!;
    const wallEnd: CoordinatePoint = selectedWall.points.get(1)!;

    const measurement = compose(
      formatLinearLabel(unitOfMeasure, precision),
      multiplyBy(gridSizeInFeet),
      ([a, b]: CoordinatePoint[]) => distance(a, b),
    )([wallStart, wallEnd]);

    const unitOfMeasurementText = (): string => {
      if (unitOfMeasure === 'decimalFeet') return ' Feet/Decimal';
      if (unitOfMeasure === 'feet') return ' Feet/Inches';

      return 'Meters';
    };

    return (
      <BackdropAnimated
        onPointerDown={cancelEvent}
        onPointerMove={cancelEvent}
        onPointerUp={cancelEvent}
        onPointerLeave={cancelEvent}
        onWheel={cancelEvent}
        onDragOver={cancelEvent}
        onDrop={cancelEvent}
        pose="enter"
        initialPose="exit"
      >
        <ModalAnimated
          pose="enter"
          initialPose="exit"
          position={window.screen.width > 400 ? position : { top: '15rem', left: '1rem' }}
          poseKey={position}
          ref={this.modalRef}
        >
          <h2>
            <CloseButton onClick={this.close} title={messages.closeModal}>
              <IconClose />
            </CloseButton>
            <span>{messages.resizeSketchTitle}</span>
          </h2>
          <p>
            Current wall size is
            <b>{` ${measurement}`}</b>
            <br />
          </p>
          <p>New wall size</p>
          <form onSubmit={this.save} autoComplete="off">
            <label htmlFor="sizeInput">
              <input
                id="sizeInput"
                ref={this.focusRef}
                type="text"
                placeholder={unitOfMeasure === 'meters' ? '0.0' : '0,0 or 0.0'}
                required
                value={size}
                onChange={this.handleSizeChanged}
                onKeyDown={this.handleKeyDown}
              />
              {unitOfMeasurementText()}
            </label>
            <ButtonBig type="submit">Apply</ButtonBig>
          </form>
        </ModalAnimated>
      </BackdropAnimated>
    );
  };
}

export default connect(
  (state: RootState): StateProps => ({
    selectedWall: selectionSelectors.getSelectedLine(state),
    canResizeSketch: selectionSelectors.isLineWallSelected(state),
    unitOfMeasure: settingsSelectors.getUnitOfMeasure(state),
    precision: settingsSelectors.getPrecision(state),
    gridSizeInFeet: settingsSelectors.getGridSizeInFeet(state),
  }),
  {
    close: resizeSketchModalActions.hideResize,
    resizeSketch: resizeSketchActions.resizeSketch,
  },
)(ResizeSketchModal);
